import React from 'react';
import type { Conversation } from '@/types/database';
import { cn } from '@/lib/utils';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (conversation: Conversation) => void;
}

export function ConversationList({ conversations, selectedId, onSelect }: ConversationListProps) {
  const formatParticipantNames = (participants: any[]) => {
    // Filter out the current user
    const otherParticipants = participants
      .filter(p => p.user.id !== participants[0].user.id)
      .map(p => p.user.full_name);

    if (otherParticipants.length === 0) return 'No participants';
    if (otherParticipants.length === 1) return otherParticipants[0];
    if (otherParticipants.length === 2) return otherParticipants.join(' & ');
    return `${otherParticipants[0]} & ${otherParticipants.length - 1} others`;
  };

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-center text-gray-500 dark:text-gray-400">
        No conversations yet
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {conversations.map((conversation) => {
        const otherParticipants = conversation.participants
          .filter((p) => p.user.id !== conversation.participants[0].user.id);
        const lastParticipant = otherParticipants[otherParticipants.length - 1];
        
        return (
          <button
            key={conversation.id}
            onClick={() => onSelect(conversation)}
            className={cn(
              "w-full p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors relative border-b dark:border-dark-border",
              selectedId === conversation.id && "bg-royal-purple/5 dark:bg-royal-purple/10"
            )}
          >
            {otherParticipants.length === 1 ? (
              <img
                src={lastParticipant.user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${lastParticipant.user.full_name}`}
                alt={lastParticipant.user.full_name}
                className="w-12 h-12 rounded-full ring-2 ring-royal-purple/20 dark:ring-royal-purple/40"
              />
            ) : (
              <div className="relative w-12 h-12">
                {otherParticipants.slice(0, 2).map((participant, index) => (
                  <img
                    key={participant.user.id}
                    src={participant.user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${participant.user.full_name}`}
                    alt={participant.user.full_name}
                    className={cn(
                      "w-8 h-8 rounded-full ring-2 ring-white dark:ring-dark-paper absolute",
                      index === 0 ? "top-0 left-0" : "bottom-0 right-0"
                    )}
                  />
                ))}
                {otherParticipants.length > 2 && (
                  <div className="absolute bottom-0 right-0 w-5 h-5 bg-royal-purple text-white text-xs font-medium rounded-full flex items-center justify-center">
                    +{otherParticipants.length - 2}
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {formatParticipantNames(conversation.participants)}
                </h3>
                {conversation.unread_count > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-royal-purple text-white text-xs font-medium rounded-full">
                    {conversation.unread_count}
                  </span>
                )}
              </div>
              {conversation.last_message && (
                <p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-0.5">
                  {conversation.last_message}
                </p>
              )}
            </div>

            <div className="text-right flex-shrink-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(conversation.last_message_at).toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}